<template>
    <div class="promo-code-block">
        <div class="promo-code-header">
            <span class="header-text">Have a promo code?</span>
        </div>
        
        <div class="promo-code-input-container">
            <input
                v-model="promoCodeInput"
                type="text"
                placeholder="Enter promo code"
                class="promo-code-input"
                :disabled="isValidating || promoCodeApplied"
                @keyup.enter="validatePromoCode"
                @input="handleInput"
            />
            
            <button
                v-if="!promoCodeApplied"
                class="apply-button"
                :disabled="!promoCodeInput.trim() || isValidating"
                @click="validatePromoCode"
            >
                <span v-if="isValidating">Validating...</span>
                <span v-else>Apply</span>
            </button>
            
            <button
                v-else
                class="remove-button"
                @click="removePromoCode"
                aria-label="Remove promo code"
            >
                <XMarkIcon class="w-5 h-5" />
            </button>
        </div>
        
        <!-- Success Message -->
        <div v-if="promoCodeApplied && promoCodeData" class="success-message">
            <i class="pi pi-check-circle"></i>
            <span>
                Promo code applied! 
                <template v-if="promoCodeData.discount_type === 'percentage'">
                    {{ promoCodeData.discount_value }}% off
                </template>
                <template v-else>
                    ${{ (promoCodeData.discount_value / 100).toFixed(2) }} off
                </template>
                <template v-if="promoCodeData.duration_cycles > 1">
                    for {{ promoCodeData.duration_cycles }} billing cycles
                </template>
            </span>
        </div>
        
        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message">
            <i class="pi pi-exclamation-circle"></i>
            <span>{{ errorMessage }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';

const subscribeStore = useSubscribeStore();
const { selectedPlanType, selectedTerm, promoCode, promoCodeData, promoCodeApplied } = storeToRefs(subscribeStore);

const promoCodeInput = ref('');
const isValidating = ref(false);
const errorMessage = ref('');

// Check for promo code from URL parameter on mount
onMounted(() => {
    const urlPromoCode = subscribeStore.urlPromoCode;
    if (urlPromoCode) {
        promoCodeInput.value = urlPromoCode;
        // Auto-validate if we have all required data
        if (selectedPlanType.value && selectedTerm.value) {
            validatePromoCode();
        }
    }
});

// Also watch for urlPromoCode changes (in case it's set after component mounts)
watch(() => subscribeStore.urlPromoCode, (newCode) => {
    if (newCode && !promoCodeApplied.value) {
        promoCodeInput.value = newCode;
        // Auto-validate if we have all required data
        if (selectedPlanType.value && selectedTerm.value) {
            validatePromoCode();
        }
    }
});

// Watch for changes in selected plan/term to validate or re-validate
watch([selectedPlanType, selectedTerm], () => {
    // If promo is already applied, re-validate with new plan/term
    if (promoCodeApplied.value) {
        validatePromoCode();
    } 
    // If we have a URL promo code that hasn't been validated yet, try now
    else if (promoCodeInput.value && selectedPlanType.value && selectedTerm.value) {
        validatePromoCode();
    }
});

const handleInput = () => {
    errorMessage.value = '';
    // Convert to uppercase as we type
    promoCodeInput.value = promoCodeInput.value.toUpperCase();
};

const validatePromoCode = async () => {
    if (!promoCodeInput.value.trim()) {
        return;
    }

    if (!selectedPlanType.value || !selectedTerm.value) {
        errorMessage.value = 'Please select a license type and term first';
        return;
    }

    isValidating.value = true;
    errorMessage.value = '';

    try {
        const isValid = await subscribeStore.validatePromoCode(promoCodeInput.value);
        
        if (isValid && promoCodeData.value) {
            promoCode.value = promoCodeInput.value;
            promoCodeApplied.value = true;
            errorMessage.value = ''; // Clear any previous error
        } else {
            errorMessage.value = 'Invalid or expired promo code';
            promoCodeApplied.value = false;
            promoCode.value = null;
            promoCodeData.value = null;
        }
    } catch (error: any) {
        // API errors (404, 400, etc.)
        errorMessage.value = error.data?.errors?.[0]?.message || error.message || 'Failed to validate promo code';
        promoCodeApplied.value = false;
        promoCode.value = null;
        promoCodeData.value = null;
    } finally {
        isValidating.value = false;
    }
};

const removePromoCode = () => {
    promoCodeInput.value = '';
    promoCode.value = null;
    promoCodeData.value = null;
    promoCodeApplied.value = false;
    errorMessage.value = '';
};
</script>

<style lang="scss" scoped>
.promo-code-block {
    @apply flex flex-col gap-3;
}

.promo-code-header {
    .header-text {
        @apply text-sm font-medium text-zinc-700;
    }
}

.promo-code-input-container {
    @apply flex gap-2;
}

.promo-code-input {
    @apply flex-1 px-3 py-2 border border-zinc-300 rounded-md text-sm;
    @apply focus:outline-none focus:ring-2 focus:ring-blue-500;
    @apply disabled:bg-zinc-100 disabled:cursor-not-allowed;
    
    &::placeholder {
        @apply text-zinc-400;
    }
}

.apply-button {
    @apply px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium;
    @apply hover:bg-blue-700 transition-colors;
    @apply disabled:bg-zinc-300 disabled:cursor-not-allowed;
}

.remove-button {
    @apply px-3 py-2 bg-red-600 text-white rounded-md text-sm;
    @apply hover:bg-red-700 transition-colors;
    @apply flex items-center justify-center;
}

.success-message {
    @apply flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md;
    
    i {
        @apply text-green-600;
    }
    
    span {
        @apply text-sm text-green-700 font-medium;
    }
}

.error-message {
    @apply flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md;
    
    i {
        @apply text-red-600;
    }
    
    span {
        @apply text-sm text-red-700;
    }
}
</style>

